import { atom } from 'jotai';
import {
  type TemplateQualityGateClinicianDecision,
  type TemplateQualityGateContext,
  type TemplateQualityGateDecision,
  type TemplateQualityGateRegionStatus,
  type TemplateQualityGateSaveContext,
  type TemplateQualityGateSurface,
} from '../../contracts';

/**
 * The gate's own state, and a name-intact re-export of its vocabulary.
 *
 * The six decision/surface/context types this module used to declare moved to
 * `packages/templates/src/contracts/template-quality-gate-analytics.types.ts`,
 * because they are `template_quality_gate`'s payload vocabulary: a payload type
 * is not self-contained without them, and a duplicated union is how one event
 * becomes two. They are re-exported here because every importer names them at
 * this path, including `jest.mock` literals.
 */
export type {
  TemplateQualityGateClinicianDecision,
  TemplateQualityGateContext,
  TemplateQualityGateDecision,
  TemplateQualityGateRegionStatus,
  TemplateQualityGateSaveContext,
  TemplateQualityGateSurface,
};

/**
 * Resolver for the in-flight `requireAcceptance()` promise. Non-null exactly
 * while a save is suspended on the attestation, so it doubles as the dialog's
 * visibility — one piece of state that cannot disagree with itself. Wrapped in
 * an object so `set` never mistakes the callback for a Jotai updater function.
 *
 * It carries the raising save's context because the dialog is the only place
 * that knows the attestation was really put in front of a clinician, and so is
 * the only honest source for the `shown` event — but it has no idea which
 * surface asked for it.
 */
export interface TemplateQualityGateResolver {
  resolve: (decision: TemplateQualityGateDecision) => void;
  context: TemplateQualityGateSaveContext;
}

export const templateQualityGateResolverAtom = atom<TemplateQualityGateResolver | null>(
  null
);
